/**
 * ShopManager - Handles NPC shop functionality
 * Extracted from NPCInteractionUI.tsx
 */

import { NPC, ShopItem } from '../../../../shared/NPCData';

export interface ShopState {
  items: ShopItem[];
  selectedItem: ShopItem | null;
  cart: Map<string, number>;
  totalCost: number;
}

export interface ShopPurchase {
  item: ShopItem;
  quantity: number;
  totalPrice: number;
}

export class ShopManager {
  private state: ShopState;
  private onShopPurchase?: (purchase: ShopPurchase) => void;
  
  constructor(npc: NPC, onShopPurchase?: (purchase: ShopPurchase) => void) {
    this.onShopPurchase = onShopPurchase;
    this.state = {
      items: npc.shopItems || [],
      selectedItem: null,
      cart: new Map(),
      totalCost: 0
    };
  }
  
  /**
   * Select an item
   */
  selectItem(item: ShopItem): void {
    this.state.selectedItem = item;
  }
  
  /**
   * Add item to cart
   */
  addToCart(item: ShopItem, quantity: number = 1): void {
    const currentQuantity = this.state.cart.get(item.id) || 0;
    this.state.cart.set(item.id, currentQuantity + quantity);
    this.updateTotalCost();
  }
  
  /**
   * Remove item from cart
   */
  removeFromCart(itemId: string, quantity: number = 1): void {
    const currentQuantity = this.state.cart.get(itemId) || 0;
    const newQuantity = Math.max(0, currentQuantity - quantity);
    
    if (newQuantity === 0) {
      this.state.cart.delete(itemId);
    } else {
      this.state.cart.set(itemId, newQuantity);
    }
    
    this.updateTotalCost();
  }
  
  /**
   * Purchase items in cart
   */
  purchaseCart(): ShopPurchase[] {
    const purchases: ShopPurchase[] = [];
    
    this.state.cart.forEach((quantity, itemId) => {
      const item = this.state.items.find(i => i.id === itemId);
      if (item) {
        const purchase: ShopPurchase = {
          item,
          quantity,
          totalPrice: (item.price || 0) * quantity
        };
        purchases.push(purchase);
        
        // Notify purchase
        if (this.onShopPurchase) {
          this.onShopPurchase(purchase);
        }
      }
    });
    
    // Clear cart after purchase
    this.clearCart();
    
    return purchases;
  }
  
  /**
   * Purchase single item
   */
  purchaseItem(item: ShopItem): ShopPurchase {
    const purchase: ShopPurchase = {
      item,
      quantity: 1,
      totalPrice: item.price || 0
    };
    
    if (this.onShopPurchase) {
      this.onShopPurchase(purchase);
    }
    
    return purchase;
  }
  
  /**
   * Clear cart
   */
  clearCart(): void {
    this.state.cart.clear();
    this.state.totalCost = 0;
  }
  
  /**
   * Update total cost
   */
  private updateTotalCost(): void {
    let total = 0;
    this.state.cart.forEach((quantity, itemId) => {
      const item = this.state.items.find(i => i.id === itemId);
      if (item) {
        total += (item.price || 0) * quantity;
      }
    });
    this.state.totalCost = total;
  }
  
  /**
   * Filter items by category
   */
  filterItemsByCategory(category: string): ShopItem[] {
    return this.state.items.filter(item => item.type === category);
  }
  
  /**
   * Get available categories
   */
  getCategories(): string[] {
    const categories = new Set(this.state.items.map(item => item.type || 'misc'));
    return Array.from(categories);
  }
  
  /**
   * Get shop state
   */
  getState(): ShopState {
    return {
      items: [...this.state.items],
      selectedItem: this.state.selectedItem,
      cart: new Map(this.state.cart),
      totalCost: this.state.totalCost
    };
  }
  
  /**
   * Can player afford item
   */
  canAfford(item: ShopItem, playerGold: number): boolean {
    return playerGold >= (item.price || 0);
  }
}